import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Theme,
  useTheme,
  Grid,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  Receipt as PaymentIcon,
  CurrencyRupee as MoneyIcon,
  Note as NoteIcon,
} from "@mui/icons-material";
import CustumCard from "../../../components/common/CustumCard";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: "16px",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  width: "100%",
}));

const textFieldStyles = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    height: "40px", // Reduced height
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.23)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "& input": {
      padding: "8px 12px", // Reduced padding
      fontSize: "0.875rem", // Smaller font size
    },
  },
  "& .MuiInputLabel-outlined": {
    backgroundColor: "transparent",
    px: 0.5,
    fontSize: "0.875rem", // Smaller label font size
    "&.MuiInputLabel-shrink": {
      fontSize: "0.75rem", // Smaller shrunk label font size
    },
  },
  "& .MuiSelect-select": {
    padding: "8px 12px", // Reduced padding for select
    fontSize: "0.875rem", // Smaller font size for select
    height: "40px", // Match input height
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    fontSize: "1.25rem", // Smaller dropdown icon
  },
});

// Separate styles for Select components
const selectStyles = (theme: Theme) => ({
  height: "40px", // Reduced height
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.23)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.87)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiSelect-select": {
    padding: "8px 12px", // Reduced padding
    fontSize: "0.875rem", // Smaller font size
    height: "40px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    fontSize: "1.25rem", // Smaller dropdown icon
  },
  "& .MuiInputLabel-outlined": {
    backgroundColor: "transparent",
    px: 0.5,
    fontSize: "0.875rem", // Smaller label font size
    "&.MuiInputLabel-shrink": {
      fontSize: "0.75rem", // Smaller shrunk label font size
    },
  },
});

interface OrderDetailsProps {
  data: {
    id: string;
    customerId: string;
    totalAmount: string | number;
    dueDate: string;
    notes: string;
    priority: "low" | "medium" | "high" | "urgent";
    paymentStatus: "pending" | "partial" | "paid";
    progressPercentage: number;
    totalItems: number;
    completedItems: number;
    products: Array<"door" | "window" | "ventilator" | "net fix">;
  };
  selectedOrder?: Order | null;
  setData: (data: {
    customerId: string;
    totalAmount: string | number;
    dueDate: string;
    notes: string;
    priority: "low" | "medium" | "high" | "urgent";
    paymentStatus: "pending" | "partial" | "paid";
    progressPercentage: number;
    totalItems: number;
    completedItems: number;
    products: Array<"door" | "window" | "ventilator" | "net fix">;
  }) => void;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  data,
  selectedOrder,
  setData,
}) => {
  const theme = useTheme(); // ✅ fix: get theme from MUI
  const [orderData, setOrderData] = useState({
    totalAmount: data?.totalAmount || "",
    dueDate: data?.dueDate || new Date().toISOString().split("T")[0],
    notes: data?.notes || "",
    priority: (data?.priority || "medium") as
      | "low"
      | "medium"
      | "high"
      | "urgent",
    paymentStatus: (data?.paymentStatus || "pending") as
      | "pending"
      | "partial"
      | "paid",
  });

  // Initialize orderData when data prop changes (but only if it's different to avoid loops)
  useEffect(() => {
    if (
      data &&
      (data.totalAmount !== orderData.totalAmount ||
        data.dueDate !== orderData.dueDate ||
        data.notes !== orderData.notes ||
        data.priority !== orderData.priority ||
        data.paymentStatus !== orderData.paymentStatus)
    ) {
      setOrderData({
        totalAmount: data.totalAmount || "",
        dueDate: data.dueDate || new Date().toISOString().split("T")[0],
        notes: data.notes || "",
        priority: (data.priority || "medium") as
          | "low"
          | "medium"
          | "high"
          | "urgent",
        paymentStatus: (data.paymentStatus || "pending") as
          | "pending"
          | "partial"
          | "paid",
      });
    }
  }, [
    data?.totalAmount,
    data?.dueDate,
    data?.notes,
    data?.priority,
    data?.paymentStatus,
  ]);

  // Initialize orderData from selectedOrder when in edit mode
  useEffect(() => {
    if (selectedOrder && !data) {
      // console.log("=== INITIALIZING ORDER DETAILS FROM SELECTED ORDER ===");
      // console.log("Selected order:", selectedOrder);

      setOrderData({
        totalAmount: selectedOrder.totalAmount?.toString() || "",
        dueDate: selectedOrder.dueDate
          ? selectedOrder.dueDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
        notes: selectedOrder.notes || "",
        priority: (selectedOrder.priority || "medium") as
          | "low"
          | "medium"
          | "high"
          | "urgent",
        paymentStatus: (selectedOrder.paymentStatus || "pending") as
          | "pending"
          | "partial"
          | "paid",
      });
    }
  }, [selectedOrder]);

  // console.log(
  //   "Select value:***-*--*--",
  //   orderData.paymentStatus,
  //   selectedOrder?.paymentStatus
  // );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CustumCard>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <MapPinIcon color="primary" width={22} />
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18 }}>
                  Sites Information
                </Typography>
              </Box> */}
          <ClipboardDocumentListIcon color="primary" width={22} />
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18 }}>
            Order Details
          </Typography>
        </Box>

        {/* Main Content */}
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 3.5, flex: 1 }}
        >
          {/* First Row: Priority + Due Date */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <FormControl fullWidth sx={selectStyles(theme)}>
              <InputLabel id="priority-label" sx={{ px: 1 }}>
                Priority
              </InputLabel>
              <Select
                labelId="priority-label"
                value={selectedOrder?.priority || orderData.priority}
                onChange={(e) => {
                  const value = e.target.value as
                    | "low"
                    | "medium"
                    | "high"
                    | "urgent";
                  const updated = { ...orderData, priority: value };
                  setOrderData(updated);
                  setData({ ...data, ...updated });
                }}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={
                selectedOrder?.dueDate
                  ? selectedOrder.dueDate.split("T")[0]
                  : orderData.dueDate
              }
              onChange={(e) => {
                const updated = { ...orderData, dueDate: e.target.value };
                setOrderData(updated);
                setData({ ...data, ...updated });
              }}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles(theme)}
            />
          </Box>

          {/* Second Row: Total Amount + Payment Status */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label="Total Amount"
              value={orderData.totalAmount || selectedOrder?.totalAmount || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d*$/.test(value)) {
                  const updated = { ...orderData, totalAmount: value };
                  setOrderData(updated);
                  setData({ ...data, ...updated });
                }
                // else → do nothing (invalid character not accepted)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles(theme)}
            />


            <FormControl fullWidth sx={selectStyles(theme)}>
              <InputLabel id="payment-status-label" sx={{ px: 1 }}>
                Payment Status
              </InputLabel>
              <Select
                labelId="payment-status-label"
                value={
                  selectedOrder?.paymentStatus ??
                  orderData.paymentStatus ??
                  "pending"
                }
                onChange={(e) => {
                  const updated = {
                    ...orderData,
                    paymentStatus: e.target.value as
                      | "pending"
                      | "partial"
                      | "paid",
                  };
                  setOrderData(updated);
                  setData({ ...data, ...updated });
                }}
                label="Payment Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partially Paid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Notes at bottom with 1 row */}
          <TextField
            fullWidth
            multiline
            rows={1}
            label="Notes"
            value={orderData.notes || selectedOrder?.notes}
            onChange={(e) => {
              const updated = { ...orderData, notes: e.target.value };
              setOrderData(updated);
              setData({ ...data, ...updated });
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <NoteIcon />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles(theme)}
          />
        </Box>
      </CustumCard>
    </Box>
  );
};

export default OrderDetails;
